import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { mainSiteUrl } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hoobuy.living';
const guideUrl = `${siteUrl}/guia-de-compras`;

export default function ShoppingGuide() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const steps = [
    {
      num: '01',
      title: 'Escolha uma Categoria',
      desc: 'Navegue pela nossa Hoobuy Spreadsheet e escolha entre 14 categorias de produtos: tênis, roupas, hoodies, jaquetas, acessórios e muito mais. Cada categoria reúne os melhores produtos verificados pela nossa equipe.',
      icon: 'ri-grid-line',
    },
    {
      num: '02',
      title: 'Encontre o Produto',
      desc: 'Dentro da categoria escolhida, explore os produtos disponíveis. Nossa planilha inclui fotos, avaliações e informações detalhadas para ajudar na sua decisão de compra.',
      icon: 'ri-search-line',
    },
    {
      num: '03',
      title: 'Copie o Link',
      desc: 'Copie o link do produto desejado diretamente da nossa planilha. Todos os links são verificados diariamente para garantir que funcionam corretamente.',
      icon: 'ri-file-copy-line',
    },
    {
      num: '04',
      title: 'Cole na Hoobuy',
      desc: 'Acesse a plataforma Hoobuy e cole o link copiado. O produto aparecerá com todas as informações: preço, tamanhos disponíveis, cores e fotos.',
      icon: 'ri-link',
    },
    {
      num: '05',
      title: 'Adicione ao Carrinho',
      desc: 'Escolha o tamanho e a cor desejada, depois adicione o produto ao seu carrinho de compras na plataforma Hoobuy.',
      icon: 'ri-shopping-cart-line',
    },
    {
      num: '06',
      title: 'Solicite Inspeção',
      desc: 'Antes do envio, solicite a inspeção gratuita do produto. A Hoobuy enviará fotos reais para você aprovar antes do despacho.',
      icon: 'ri-image-line',
    },
    {
      num: '07',
      title: 'Finalize o Pagamento',
      desc: 'Após aprovar as fotos de inspeção, escolha o método de pagamento e finalize a compra com total segurança.',
      icon: 'ri-bank-card-line',
    },
    {
      num: '08',
      title: 'Receba na sua Casa',
      desc: 'Acompanhe o rastreamento do envio e receba seu produto no conforto da sua casa. O frete internacional é rápido e seguro.',
      icon: 'ri-truck-line',
    },
  ];

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      <header className="relative w-full pt-24 pb-14 md:pt-32 md:pb-18 bg-foreground-950 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=Clean%20organized%20shopping%20guide%20concept%20with%20modern%20devices%20and%20fashion%20moodboard%20on%20warm%20cream%20desk%2C%20soft%20natural%20window%20light%2C%20minimalist%20creative%20workspace%2C%20harmonious%20composition%2C%20lifestyle%20photography%20aesthetic%2C%20neutral%20warm%20tones%2C%20peaceful%20productive%20atmosphere&width=1400&height=600&seq=guia-compras-header-01&orientation=landscape"
            alt="Guia de Compras Hoobuy"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/90 via-foreground-950/70 to-foreground-950"></div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <nav className="flex items-center justify-center gap-2 text-xs md:text-sm mb-6" aria-label="Breadcrumb">
            <Link to="/" className="text-white/50 hover:text-white/80 cursor-pointer transition-colors">
              Início
            </Link>
            <span className="text-white/30">/</span>
            <span className="text-white/80">Guia de Compras</span>
          </nav>
          <h1 className="text-2xl sm:text-3xl md:text-5xl font-heading font-bold text-white leading-tight">
            Guia de Compras Hoobuy
          </h1>
          <p className="mt-4 text-sm md:text-base text-white/60 max-w-xl mx-auto leading-relaxed">
            Tutorial passo a passo para comprar na Hoobuy usando nossa Spreadsheet. Do início ao fim, tudo explicado de forma simples e direta.
          </p>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="space-y-6">
          {steps.map((step, i) => (
            <div key={step.num} className="flex gap-5 items-start group">
              <div className="flex-shrink-0 w-14 md:w-16 flex flex-col items-center">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-primary-100 flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                  <i className={`${step.icon} text-xl text-primary-600`}></i>
                </div>
                {i < steps.length - 1 && (
                  <div className="w-px h-8 md:h-10 bg-primary-200 mt-1.5"></div>
                )}
              </div>
              <div className="flex-1 bg-background-50 border border-background-200/60 rounded-xl p-5 md:p-6 group-hover:border-primary-200/60 transition-all">
                <span className="text-xs font-bold text-primary-500 uppercase tracking-wider">Passo {step.num}</span>
                <h3 className="mt-1 text-base md:text-lg font-semibold text-foreground-950">{step.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 cursor-pointer whitespace-nowrap transition-all duration-300"
          >
            Começar a Comprar Agora
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </section>

      <section className="w-full py-14 md:py-20 bg-background-100">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
            Mais Guias e Artigos
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {articles.slice(0, 4).map((article) => (
              <Link
                key={article.slug}
                to={`/artigo/${article.slug}`}
                className="flex items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200/60 hover:border-primary-200/60 cursor-pointer transition-all"
              >
                <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-950 line-clamp-2 leading-snug">{article.title}</h4>
                  <span className="text-xs text-foreground-400 mt-1">{article.readTime} de leitura</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <script type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            'name': 'Guia de Compras Hoobuy',
            'description': 'Tutorial passo a passo para comprar na Hoobuy usando nossa Spreadsheet. Do início ao fim, tudo explicado de forma simples e direta.',
            'step': steps.map((step, i) => ({
              '@type': 'HowToStep',
              'position': i + 1,
              'name': step.title,
              'itemListElement': {
                '@type': 'HowToDirection',
                'text': step.desc,
              },
            })),
          }),
        }}
      />
      <script type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            'itemListElement': [
              {
                '@type': 'ListItem',
                'position': 1,
                'name': 'Início',
                'item': siteUrl,
              },
              {
                '@type': 'ListItem',
                'position': 2,
                'name': 'Guia de Compras',
                'item': guideUrl,
              },
            ],
          }),
        }}
      />

      <Footer />
    </main>
  );
}